import React, { forwardRef, useContext, useState } from 'react';
import { cn } from '@/utils/cn';
import { tokens } from '@/theme/tokens';
import type { TextareaProps } from './Textarea.types';
import { ThemeContext } from '@/context/ThemeContext';

/**
 * # Textarea
 *
 * Vice-radkovy textovy vstup.
 *
 * @example
 * <Textarea label="Poznamka" rows={4} />
 * <Textarea label="Bio" maxLength={200} showCount />
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      hint,
      size = 'md',
      autoResize,
      maxLength,
      showCount,
      fullWidth = true,
      className,
      id,
      required,
      disabled,
      onChange,
      value,
      'data-testid': testId,
      ...nativeProps
    },
    ref,
  ) => {
    const textareaId = id || `textarea-${label?.toLowerCase().replace(/\s+/g, '-')}`;
    const hasError = !!error;
    const theme = useContext(ThemeContext);
    const isDark = theme?.mode === 'dark';
    const themedForegroundClass = isDark ? 'ui-field-foreground-dark' : 'ui-field-foreground-light';
    const defaultThemeClasses = isDark
      ? 'border-zinc-600 bg-zinc-800 text-zinc-100'
      : 'border-gray-300 bg-white text-gray-900';
    const errorThemeClasses = isDark
      ? 'border-red-400 bg-red-950/20 text-zinc-100'
      : 'border-red-500 bg-red-50 text-gray-900';
    const disabledThemeClasses = isDark ? 'bg-zinc-800/50' : 'bg-gray-50';

    // Internal state for char count if value is not controlled or for display
    const [charCount, setCharCount] = useState(typeof value === 'string' ? value.length : 0);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setCharCount(e.target.value.length);

      if (autoResize) {
        e.target.style.height = 'auto';
        e.target.style.height = `${e.target.scrollHeight}px`;
      }

      onChange?.(e);
    };

    return (
      <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
        {/* Label */}
        {label && (
          <label
            htmlFor={textareaId}
            className={cn(
              'text-sm font-medium',
              'text-gray-700 dark:text-zinc-200',
              disabled && 'opacity-50',
            )}
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <textarea
            ref={ref}
            id={textareaId}
            disabled={disabled}
            maxLength={maxLength}
            aria-describedby={hasError ? `${textareaId}-error` : undefined}
            onChange={handleChange}
            value={value}
            data-testid={testId}
            className={cn(
              // Base
              'w-full border outline-none min-h-[80px]',
              'transition-all duration-200',
              'placeholder:text-gray-400 dark:placeholder:text-zinc-500',
              themedForegroundClass,
              tokens.radius.sm,
              // Size
              sizeClasses[size],
              // State
              hasError ? errorThemeClasses : defaultThemeClasses,
              // Disabled
              disabled && 'opacity-50 cursor-not-allowed',
              disabled && disabledThemeClasses,
              // Resize
              autoResize ? 'resize-none overflow-hidden' : '',
              className,
            )}
            {...nativeProps}
          />
        </div>

        {/* Footer: Error/Hint + Count */}
        <div className="flex justify-between items-start text-sm">
          <div className="flex-1">
            {hasError && (
              <p id={`${textareaId}-error`} className="text-red-500 dark:text-red-400" role="alert">
                {error}
              </p>
            )}
            {!hasError && hint && (
              <p id={`${textareaId}-hint`} className="text-gray-500 dark:text-zinc-400">
                {hint}
              </p>
            )}
          </div>

          {showCount && maxLength && (
            <span
              className={cn(
                'ml-4 text-xs tabular-nums text-gray-500 dark:text-zinc-400',
                charCount > maxLength && 'text-red-500',
              )}
            >
              {charCount} / {maxLength}
            </span>
          )}
        </div>
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';

// --- Style mapy ---

const sizeClasses: Record<string, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-3.5 py-2.5 text-sm',
  lg: 'px-4 py-3 text-base',
};

